import React, { useEffect, useRef, useState } from 'react';
import { Stage, Layer, Image as KonvaImage, Rect, Text, Group, Transformer } from 'react-konva';

function useImage(src) {
  const [img, setImg] = useState(null);
  useEffect(() => {
    if (!src) { setImg(null); return; }
    const image = new window.Image();
    image.crossOrigin = 'anonymous';
    image.src = src;
    image.onload = () => setImg(image);
    return () => { image.onload = null; };
  }, [src]);
  return img;
}

function TransformableNode({ element, isSelected, onSelect, onChange, children }) {
  const groupRef = useRef();
  const trRef = useRef();

  useEffect(() => {
    if (isSelected && trRef.current && groupRef.current) {
      trRef.current.nodes([groupRef.current]);
      trRef.current.getLayer().batchDraw();
    }
  }, [isSelected]);

  return (
    <>
      <Group
        ref={groupRef}
        x={element.x}
        y={element.y}
        draggable
        onClick={onSelect}
        onTap={onSelect}
        onDragEnd={(e) => onChange({ ...element, x: Math.round(e.target.x()), y: Math.round(e.target.y()) })}
        onTransformEnd={() => {
          const node = groupRef.current;
          const scaleX = node.scaleX();
          const scaleY = node.scaleY();
          node.scaleX(1);
          node.scaleY(1);
          onChange({
            ...element,
            x: Math.round(node.x()),
            y: Math.round(node.y()),
            width:  Math.max(20, Math.round(element.width  * scaleX)),
            height: Math.max(20, Math.round(element.height * scaleY)),
          });
        }}
      >
        {children}
      </Group>
      {isSelected && (
        <Transformer
          ref={trRef}
          rotateEnabled={false}
          boundBoxFunc={(oldBox, newBox) => (newBox.width < 20 || newBox.height < 20 ? oldBox : newBox)}
        />
      )}
    </>
  );
}

function TextElement({ element, isSelected, onSelect, onChange }) {
  const display = (element.text_content || '').replace('{student_name}', "Student");

  return (
    <TransformableNode element={element} isSelected={isSelected} onSelect={onSelect} onChange={onChange}>
      <Rect
        width={element.width}
        height={element.height}
        stroke={isSelected ? '#1976d2' : '#ffffff'}
        dash={[6, 4]}
        strokeWidth={1}
        fill="rgba(255,255,255,0.08)"
      />
      <Text
        width={element.width}
        height={element.height}
        text={display || 'Text'}
        fontSize={element.font_size || 40}
        fontStyle="bold"
        fill={element.color || '#ffffff'}
        align="center"
        verticalAlign="middle"
        wrap={element.fit_mode === 'word_wrap' ? 'word' : 'none'}
      />
    </TransformableNode>
  );
}

function PhotoElement({ element, isSelected, onSelect, onChange }) {
  return (
    <TransformableNode element={element} isSelected={isSelected} onSelect={onSelect} onChange={onChange}>
      <Rect
        width={element.width}
        height={element.height}
        stroke="#ffffff"
        dash={[6, 4]}
        strokeWidth={isSelected ? 2 : 1}
        fill="rgba(255,255,255,0.12)"
      />
      <Text
        width={element.width}
        height={28}
        y={element.height / 2 - 20}
        text="👤"
        align="center"
        fontSize={24}
        fill="#ffffff"
      />
      <Text
        width={element.width}
        height={20}
        y={element.height / 2 + 4}
        text={element.label || 'Student Photo'}
        align="center"
        fontSize={12}
        fill="#ffffff"
        fontStyle="bold"
      />
      <Text
        width={element.width}
        height={16}
        y={element.height / 2 + 22}
        text={`Pose ${element.pose_number || 1}`}
        align="center"
        fontSize={11}
        fill="#cccccc"
      />
    </TransformableNode>
  );
}

export default function Canvas({
  width, height, backgroundUrl, elements, selectedId,
  onSelect, onChangeElement,
}) {
  const bgImage = useImage(backgroundUrl);
  const containerRef = useRef();
  const [stageWidth, setStageWidth] = useState(width);

  useEffect(() => {
    if (!containerRef.current) return undefined;
    let frame = 0;
    const observer = new ResizeObserver((entries) => {
      cancelAnimationFrame(frame);
      const next = Math.min(width, entries[0].contentRect.width - 32);
      frame = requestAnimationFrame(() => setStageWidth(next));
    });
    observer.observe(containerRef.current);
    return () => { cancelAnimationFrame(frame); observer.disconnect(); };
  }, [width]);

  const scale = stageWidth / width;
  const stageHeight = height * scale;

  return (
    <div ref={containerRef} style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#1f2a36', padding: 16, overflow: 'auto', minHeight: 0 }}>
      <div style={{ background: '#111827', boxShadow: '0 10px 30px rgba(0,0,0,0.4)', borderRadius: 6 }}>
        <Stage
          width={stageWidth}
          height={stageHeight}
          scaleX={scale}
          scaleY={scale}
          onMouseDown={(e) => { if (e.target === e.target.getStage()) onSelect(null); }}
        >
          <Layer listening={false}>
            <Rect width={width} height={height} fill="#273142" />
            {bgImage && <KonvaImage image={bgImage} width={width} height={height} />}
          </Layer>
          <Layer>
            {elements.map((el) => {
              const common = {
                element: el,
                isSelected: el.id === selectedId,
                onSelect: () => onSelect(el.id),
                onChange: (next) => onChangeElement(next),
              };
              return el.type === 'text'
                ? <TextElement key={el.id} {...common} />
                : <PhotoElement key={el.id} {...common} />;
            })}
          </Layer>
        </Stage>
      </div>
    </div>
  );
}
